import {
  ExternalLinkIcon,
  InfoIcon,
  OctagonAlertIcon,
  TriangleAlertIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  SidebarWrapper,
  SidebarWrapperOverflowContainer,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import useDatasources from '@/hooks/useDatasources';
import { cn } from '@/lib/utils';
import { selectDatasourceId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { DatasourceType, ReportActionName, ReportSeverity } from '@/shared/types/collections';
import { EXCLUDED_URLS_URL, getDocsUrl } from '@/util/constants';
import { formatDate } from '@/util/dateUtils';
import { durationFormat } from '@/util/util';

/** Blocking findings stop indexing; advisory ones cost coverage; info is us obeying the site. */
const SEVERITY_ALERT: Record<
  ReportSeverity,
  { variant: 'destructive' | 'warning' | 'info'; Icon: LucideIcon }
> = {
  blocking: { variant: 'destructive', Icon: OctagonAlertIcon },
  advisory: { variant: 'warning', Icon: TriangleAlertIcon },
  info: { variant: 'info', Icon: InfoIcon },
};

/**
 * Static keys so the copy stays greppable and translation tooling can find it. The backend
 * may add an action before this file knows about it, so the lookup can miss.
 */
const ACTION_COPY: Record<ReportActionName, { title: string; description: string }> = {
  allowlist_crawler: {
    title: 'collections.report.actions.allowlist_crawler.title',
    description: 'collections.report.actions.allowlist_crawler.description',
  },
  fix_sitemap: {
    title: 'collections.report.actions.fix_sitemap.title',
    description: 'collections.report.actions.fix_sitemap.description',
  },
  consider_excluding: {
    title: 'collections.report.actions.consider_excluding.title',
    description: 'collections.report.actions.consider_excluding.description',
  },
  review_rendering: {
    title: 'collections.report.actions.review_rendering.title',
    description: 'collections.report.actions.review_rendering.description',
  },
  no_action: {
    title: 'collections.report.actions.no_action.title',
    description: 'collections.report.actions.no_action.description',
  },
  contact_support: {
    title: 'collections.report.actions.contact_support.title',
    description: 'collections.report.actions.contact_support.description',
  },
  update_datasource_config: {
    title: 'collections.report.actions.update_datasource_config.title',
    description: 'collections.report.actions.update_datasource_config.description',
  },
};

/**
 * Copy for one cause, where the action's own wording would be wrong. Both raising the page
 * limit and dropping a dead category are `update_datasource_config`, but "this item no longer
 * exists" says nothing to a customer whose site is simply larger than the crawl. Causes are
 * free-form on the wire, so an unmapped one falls back to the action's copy.
 */
const CAUSE_COPY: Partial<Record<string, { title: string; description: string }>> = {
  not_a_sitemap: {
    title: 'collections.report.causes.not_a_sitemap.title',
    description: 'collections.report.causes.not_a_sitemap.description',
  },
  page_limit: {
    title: 'collections.report.causes.page_limit.title',
    description: 'collections.report.causes.page_limit.description',
  },
};

/**
 * Where the customer goes to actually fix it. The crawler IPs are region-specific and change
 * with our infrastructure, so this links to the page that owns them rather than pasting
 * addresses into the UI where they would quietly go stale.
 */
/** Blocking first, then advisory, then info. */
const SEVERITY_ORDER: Record<ReportSeverity, number> = { blocking: 0, advisory: 1, info: 2 };

const ACTION_DOCS: Partial<Record<ReportActionName, string>> = {
  allowlist_crawler: getDocsUrl(
    '/docs/knowledge-base/knowledge-base-website#cdn-or-waf-blocking-the-crawler'
  ),
  fix_sitemap: getDocsUrl('/docs/knowledge-base/knowledge-base-website#sitemap-urls'),
  // The page the exclude-list field already links to, so both routes send them to one place.
  consider_excluding: EXCLUDED_URLS_URL,
  review_rendering: getDocsUrl('/docs/knowledge-base/knowledge-base-website#troubleshooting'),
};

/** `allowlist_crawler` reads as "Allowlist crawler" rather than raw snake_case. */
const humanizeAction = (action: string) => {
  const spaced = action.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const IngestionReport = ({
  collectionId,
  datasourceId,
}: {
  collectionId: string;
  datasourceId: string;
}) => {
  const dispatch = useDispatch();
  const reduxDatasourceId = useSelector(selectDatasourceId);
  // URL first: redux only applies on the list page, where the URL has no datasource id.
  const datasource_id = datasourceId || reduxDatasourceId;

  // Hooks
  const { datasource } = useDatasources(collectionId, datasource_id);
  const { t } = useTranslation();

  // Local variables
  const details = datasource?.last_indexed_details;
  const report = details?.report;
  const start_time = details?.start_time || null;
  const end_time = details?.end_time || null;
  const uploadedUrls = report ? report.counts.indexed : datasource?.document_count || 0;
  // Without the parentheses this is `NaN || 0` whenever either array is absent, which silently
  // reports zero failures. The v2 report carries a real count and does not need the fallback.
  const failedUrls = report
    ? report.counts.failed
    : (details?.warnings?.length ?? 0) + (details?.errors?.length ?? 0);
  // Pages we left out on purpose: the customer's own rules, plus pages that hold no content.
  const excludedUrls = report
    ? report.counts.skipped_by_your_rules + report.counts.skipped_known_empty
    : 0;

  // Helper function to calculate duration in minutes
  const calculateDurationInMinutes = (
    startTime: string | number,
    endTime: string | number
  ): number => {
    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    return (end - start) / (1000 * 60); // Convert milliseconds to minutes
  };

  const summary = {
    startTime: start_time ? formatDate(start_time, 'dd/MM/yy HH:mm:ss') : '-',
    endTime: end_time ? formatDate(end_time, 'dd/MM/yy HH:mm:ss') : '-',
    totalTime:
      start_time && end_time
        ? durationFormat(t, calculateDurationInMinutes(start_time, end_time))
        : '-',
    // v1 only ever knew about indexed and failed. v2 also knows what it skipped, and leaving
    // those out would make the total smaller than the rows beneath it.
    totalUrls: report ? uploadedUrls + excludedUrls + failedUrls : uploadedUrls + failedUrls,
    uploadedUrls,
    failedUrls,
  };

  // Data for the report rows
  const reportRows = [
    { label: t('collections.report.start'), value: summary.startTime },
    { label: t('collections.report.end'), value: summary.endTime },
    { label: t('collections.report.total_time'), value: summary.totalTime },
    { label: t('collections.report.total_urls'), value: summary.totalUrls },
    { label: t('collections.report.uploaded_urls'), value: summary.uploadedUrls },
    { label: t('collections.report.failed_urls'), value: summary.failedUrls },
  ];

  // Only the v2 report can tell an exclusion from a failure. On v1 rows both are folded into
  // `failed_urls`, so showing the row there would double-count.
  if (report) {
    reportRows.splice(reportRows.length - 1, 0, {
      label: t('collections.report.excluded_urls'),
      value: excludedUrls,
    });
  }

  // Same row treatment as the sibling DatasourceDetails panel: muted label, semibold value.
  // The gap splits timings from counts, which is the break the design shows.
  const summaryRows = (
    <div className="flex flex-col">
      {reportRows.map((row, index) => (
        <div key={row.label} className={cn(index === 3 && 'mt-6')}>
          <div className="flex items-center justify-between gap-4 py-3">
            <span className="text-sm leading-5 font-medium text-foreground-muted">{row.label}</span>
            <span className="truncate text-sm leading-6 font-semibold">{row.value}</span>
          </div>
          {index < reportRows.length - 1 && <Separator />}
        </div>
      ))}
    </div>
  );

  // The indexer already sorts by severity. Sorting a copy here too means a producer that does
  // not — an older build, or a future one — still cannot put "nothing to do" above a blocker.
  const actionEntries = [...(report?.actions ?? [])]
    .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9))
    .map((action) => {
      // `cause` is free-form on the wire, so it is read with hasOwn: a payload naming
      // `toString` or `__proto__` would otherwise resolve to an inherited value, and `copy`
      // would be truthy with no title to render.
      let copy = ACTION_COPY[action.action];
      if (Object.hasOwn(CAUSE_COPY, action.cause)) {
        copy = CAUSE_COPY[action.cause];
      }
      const docsUrl = ACTION_DOCS[action.action];
      const { variant, Icon } = SEVERITY_ALERT[action.severity] ?? SEVERITY_ALERT.info;
      const samples = action.samples ?? action.sample_urls ?? [];
      return (
        <Alert key={`${action.action}-${action.cause}`} variant={variant}>
          <Icon />
          <AlertTitle className="line-clamp-none">
            {copy ? t(copy.title) : humanizeAction(action.action)}
          </AlertTitle>
          <AlertDescription>
            <span className="text-foreground-muted">
              {copy
                ? t(copy.description, { count: action.affected })
                : t('collections.report.actions.affected', { count: action.affected })}
            </span>
            {samples.length > 0 && (
              <ul className="mt-1 flex list-inside list-disc flex-col gap-0.5">
                {samples.map((url) => (
                  <li key={url} className="text-xs break-all text-foreground-muted">
                    {url}
                  </li>
                ))}
              </ul>
            )}
            {docsUrl && (
              <a
                href={docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-link mt-1 inline-flex items-center gap-1 text-sm font-medium hover:underline"
              >
                {t('collections.report.actions.how_to_fix')}
                <ExternalLinkIcon className="size-3.5" />
              </a>
            )}
          </AlertDescription>
        </Alert>
      );
    });

  // Only websites and external sources have reports, so anything not a website is external.
  const isExternalSource = datasource && datasource.type !== DatasourceType.WEBSITE;
  const title = isExternalSource
    ? t('collections.report.external')
    : t('collections.report.webpages');

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">{title}</h2>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          type="button"
          onClick={() => dispatch(setSidebarContent(null))}
        >
          <XIcon size={16} className="text-foreground-muted" />
        </Button>
      </SidebarWrapperTitle>
      {/* Scrolls inside the wrapper. A plain div would overflow it and paint the rows straight
          onto the host panel, which is a different colour. */}
      <SidebarWrapperOverflowContainer className="pb-6">
        {/* What to change comes first. Someone opens this report because the health column told
            them something was wrong; the counts are the context for it, not the answer. */}
        {actionEntries && actionEntries.length > 0 && (
          <div className="mb-6 flex flex-col gap-3">{actionEntries}</div>
        )}
        {summaryRows}
      </SidebarWrapperOverflowContainer>
    </SidebarWrapper>
  );
};
